import { axiosClient } from 'config/Axios/AxiosInstanceConfigs';
import { deleteRequest, getRequest, patchRequest, postRequest, putRequest } from 'config/Axios/baseConfig';
import mockData from './mockData.json';
import templateMockData from './templateMockData.json';

// Interfaces
export interface BidAnalysisProject {
  id: string;
  name: string;
  productName: string;
  contractType: string;
  status: 'draft' | 'review' | 'approved';
  originalStatus?: string; // Store original status from backend for display
  analyst: string;
  reviewer?: string;
  targetLaunch?: string;
  lastModified?: string;
  submitted?: string;
  approved?: string;
  launchDate?: string;
  progress: number;
}

export interface DashboardAnalytics {
  totalAnalyses: number;
  successRate: number;
  avgDaysToComplete: number;
  totalOpportunityValue: string;
  activeProjects: number;
}

export interface Activity {
  id: string;
  type: 'created' | 'submitted' | 'updated' | 'approved';
  title: string;
  details: string;
  timestamp: string;
}

export interface StrengthWithPackSize {
  Strength: string;
  PackSize: string[];
}

export interface OpportunityIteration {
  id: number;
  opportunityId: number;
  opportunityName: string;
  productName: string;
  /** Primary supplier name (from PrimarySupplierID). */
  primarySupplierId: string | null;
  /** Agency names (from BDDimAgency SourceName). */
  agencies: string[];
  /** Source of the opportunity iteration (e.g. "Supplier Pipeline", "NC Expiration"). */
  source?: string;
  /** Strengths and their associated pack sizes. */
  strengths?: StrengthWithPackSize[];
  /** @deprecated Use primarySupplierId for primary supplier, agencies for agencies. */
  agency?: string;
  status?: string;
  lastModified?: string;
}

// API Response interface (what backend actually returns)
interface OpportunityIterationAPIResponse {
  OpportunityIterationsID: number;
  OpportunityID: number;
  MoleculeAndForm: string;
  PrimarySupplierID: string | null;
  agencies: string[];
  Source?: string;
  Strengths?: Array<{ Strength: string; PackSize: string[] }>;
}

export interface CreateBidRequest {
  template_type: 'mas' | 'nc';
  product_type?: 'direct' | 'indirect'; // Optional for NC template
  opportunity_iteration_id: number;
}

export interface CreateBidResponse {
  message: string;
  bid_id: number;
  opportunity_iteration_id: number;
  template_type: string;
  product_type: string;
}

// Backend API response interface (what backend actually returns)
interface BidDetailsAPIResponse {
  bid_master_id: number;
  product_name: string;
  analyst: string;
  template_type: 'NC' | 'MAS Direct' | 'MAS Indirect'; // Backend returns this format
  intended_contracts?: string | null; // Backend returns comma-separated string like "CDC, DAPA"
  target_launch_date?: string | null; // Backend returns date string like "05-28-2026"
  gsms_handling_type?: string | null; // GSMS Handling Type (e.g., "Private Label CII", "Marketing Income")
  form_details: {
    form_data: Array<{
      concept_name: string;
      pack_size: Record<string, any>;
    }>;
  };
  isActive: number;
  analystBADate?: string | null;
  status?: string; // Bid status: "Draft", "Closed", "In Review", etc.
  reviewer?: string; // Reviewer email
  LastModifiedDate?: string; // Last modified date string like "1 hour ago"
  approved_by?: string | null; // Bid-level approver name (e.g. "Susnata Goswami")
  va_data_as_of?: string | null; // Latest VA market sales month/year at bid creation e.g. "10/25"
  dod_data_as_of?: string | null; // Latest DOD market sales month/year at bid creation e.g. "09/25"
}

// Frontend interface (normalized format)
export interface BidDetails {
  bid_master_id: number;
  product_name: string;
  analyst: string;
  product_type: 'direct' | 'indirect' | null; // Extracted from template_type
  template_type: 'mas' | 'nc'; // Normalized from backend response
  intended_contracts?: string | null; // Comma-separated string like "CDC, DAPA"
  target_launch_date?: string | null; // Date string from backend like "05-28-2026"
  gsms_handling_type?: string | null; // GSMS Handling Type (e.g., "Private Label CII", "Marketing Income")
  reviewer?: string; // Reviewer email
  LastModifiedDate?: string; // Last modified date string like "1 hour ago"
  form_details: {
    form_data: Array<{
      concept_name: string;
      pack_size: Record<string, any>;
    }>;
  };
  isActive: number;
  analystBADate?: string | null;
  status?: string; // Bid status: "Draft", "Closed", "In Review", etc.
  approved_by?: string | null; // Bid-level approver name (for Global Output Approved By column)
  va_data_as_of?: string | null; // Latest VA market sales data month/year at bid creation e.g. "01/25"
  dod_data_as_of?: string | null; // Latest DOD market sales data month/year at bid creation e.g. "01/25"
  // Optional owner/creator fields (may come from API in different formats)
  created_by?: string | null;
  owner?: string | null;
  user?: string | null;
  createdBy?: string | null;
  created_by_name?: string | null;
  creator?: string | null;
}

/**
 * Parse backend template_type and extract product_type and normalized template_type
 * Backend returns: "NC", "MAS Direct", or "MAS Indirect"
 * Returns: { template_type: 'mas' | 'nc', product_type: 'direct' | 'indirect' | null }
 */
function parseTemplateType(backendTemplateType: string): {
  template_type: 'mas' | 'nc';
  product_type: 'direct' | 'indirect' | null;
} {
  if (!backendTemplateType) {
    return { template_type: 'mas', product_type: null };
  }

  const templateTypeLower = backendTemplateType.toLowerCase().trim();

  // Handle NC template
  if (templateTypeLower === 'nc') {
    return { template_type: 'nc', product_type: null };
  }

  // Handle MAS templates
  if (templateTypeLower.includes('mas')) {
    if (templateTypeLower.includes('indirect')) {
      return { template_type: 'mas', product_type: 'indirect' };
    } else if (templateTypeLower.includes('direct')) {
      return { template_type: 'mas', product_type: 'direct' };
    }
    // If it's MAS but no direct/indirect specified, default to direct
    return { template_type: 'mas', product_type: 'direct' };
  }

  // Default fallback
  return { template_type: 'mas', product_type: null };
}

export interface ContractType {
  id: string;
  name: string;
  description: string;
  options: ContractOption[];
}

export interface ContractOption {
  id: string;
  label: string;
  rows: number;
  estimatedTime: string;
}

export interface Opportunity {
  id: string;
  title: string;
  agency: string;
  value: string;
  deadline: string;
  status: 'open' | 'closing-soon' | 'closed';
  competitionLevel: 'low' | 'medium' | 'high';
}

export interface ProductType {
  id: string;
  name: string;
  description: string;
  features?: string[];
}

export interface TemplateConfig {
  sections: number;
  requiredFields: number;
  autoCalculations: number;
  estimatedTime: string;
}

export interface BidAnalysisWizardState {
  contractType?: string;
  contractOption?: string;
  opportunityId?: string;
  productTypeId?: string;
  templateConfig?: TemplateConfig;
}

export interface BidAnalysisTemplate {
  projectInformation: any;
  productDetails: any;
  currentMarketState: any;
  cogsBuildUp: any;
  pricingCalculator: any;
  fssTrackingRatioValidation: any;
  packSizeWiseOutputSection: any;
  competitiveInsight: any;
  contractPriceRanking: any[];
  commercialPricing: any[];
  globalOutputSection: any[];
  totals: any;
}

// Service functions with real API calls
export const bidAnalysisServices = {
  /**
   * Get all active bids with pagination, search, and status filter
   * API: GET /bid/get-all-active-bids?page=1&page_size=10&search=...&status=...
   */
  getActiveBids: (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    status?: string;
    user?: string;
  }): Promise<{ data: BidAnalysisProject[]; count: number; total_pages?: number }> => {
    return new Promise(async (resolve, _reject) => {
      try {
        // Build query parameters
        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append('page', String(params.page));
        if (params?.page_size) queryParams.append('page_size', String(params.page_size));
        if (params?.search) queryParams.append('search', params.search);
        if (params?.status) queryParams.append('status', params.status);
        if (params?.user) queryParams.append('user', params.user);

        const url = `bid/get-all-active-bids${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        const response = await getRequest<any>(url);

        // Handle different response structures
        // Backend returns: { data: [...], pagination: { total_count, page, page_size, total_pages, ... } }
        // Axios wraps it, so response.data is { data: [...], pagination: {...} }
        let data = response.data?.data;

        // Extract count from pagination object (new structure) or fallback to old structure
        // New API structure: { data: [...], pagination: { total_count, ... } }
        let count = response.data?.pagination?.total_count;
        if (count === undefined || count === null) {
          count = response.data?.count;
        }
        const totalPages = response.data?.pagination?.total_pages || response.data?.total_pages;

        // If response.data is already an array (fallback)
        if (!data && Array.isArray(response.data)) {
          data = response.data;
          // If count is not provided and we have data, use data length
          if (count === undefined || count === null) {
            count = response.data.length;
          }
        }

        // Ensure data is an array
        if (!Array.isArray(data)) {
          console.warn(
            'getActiveBids: API returned non-array data, using empty array',
            response.data
          );
          data = [];
        }

        // If count is still not set, use data length as fallback
        if (count === undefined || count === null) {
          count = data.length;
        }

        // Calculate total pages if not provided
        const calculatedTotalPages = totalPages || Math.ceil(count / (params?.page_size || 10));

        // Map backend response to frontend interface
        const projects: BidAnalysisProject[] = data.map((item: any) => {
          // Map status from backend format to frontend format
          // Backend: "Draft", "In Review", "Needs modification", "Approved"
          // Frontend: 'draft' | 'review' | 'approved'
          const originalStatus = item.status || 'Draft';
          let status: 'draft' | 'review' | 'approved' = 'draft';
          const statusLower = originalStatus.toLowerCase();
          if (
            statusLower === 'in review' ||
            statusLower === 'in-review' ||
            statusLower === 'review'
          ) {
            status = 'review';
          } else if (statusLower === 'approved') {
            status = 'approved';
          } else if (statusLower === 'needs modification' || statusLower === 'needs-modification') {
            status = 'review'; // Map "Needs modification" to review status
          } else {
            status = 'draft';
          }

          return {
            id: String(item.bid_id || item.bid_master_id || item.id),
            name: item.molecule_name || item.product_name || item.name || 'Untitled Bid',
            productName: item.molecule_name || item.product_name || '',
            contractType: item.template_name || item.template_type || item.contract_type || 'MAS',
            status: status,
            originalStatus: originalStatus, // Store original for display
            analyst: item.analyst_name || item.analyst || 'Unknown',
            reviewer: item.reviewer_name || item.reviewer || undefined,
            targetLaunch: item.target_launch_date || item.target_launch || item.targetLaunch,
            lastModified: item.last_modified || item.lastModified,
            submitted: item.submitted || undefined,
            approved: item.approved || undefined,
            launchDate: item.launch_date || undefined,
            progress: item.progress || 0,
          };
        });

        resolve({ data: projects, count, total_pages: calculatedTotalPages });
      } catch (error) {
        console.log('in bidAnalysisServices > getActiveBids, Err===> ', error);
        // Fallback to empty result instead of mock data to show real state
        resolve({ data: [], count: 0, total_pages: 0 });
      }
    });
  },

  /**
   * Get opportunity iterations for bid
   * API: POST /bid/get-opportunity-iterations-for-bid
   */
  getOpportunityIterations: (opportunityType: 'mas' | 'nc'): Promise<OpportunityIteration[]> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await postRequest<
          { data: OpportunityIterationAPIResponse[] } | OpportunityIterationAPIResponse[]
        >('bid/get-opportunity-iterations-for-bid', { opportunity_type: opportunityType });

        // Extract data array from response
        const apiData = (response.data as any).data || response.data || [];

        // Transform API response to match our interface
        const opportunities: OpportunityIteration[] = Array.isArray(apiData)
          ? apiData.map((item: OpportunityIterationAPIResponse) => ({
              id: item.OpportunityIterationsID,
              opportunityId: item.OpportunityID,
              opportunityName: item.MoleculeAndForm,
              productName: item.MoleculeAndForm,
              primarySupplierId: item.PrimarySupplierID ?? null,
              agencies: Array.isArray(item.agencies) ? item.agencies : [],
              source: typeof item.Source === 'string' ? item.Source : '',
              strengths: Array.isArray(item.Strengths) ? item.Strengths : [],
              status: undefined,
              lastModified: undefined,
            }))
          : [];

        resolve(opportunities);
      } catch (error) {
        console.log('in bidAnalysisServices > getOpportunityIterations, Err===> ', error);
        reject(error);
      }
    });
  },

  /**
   * Create new bid
   * API: POST /bid/create-bid
   */
  createBid: (payload: CreateBidRequest): Promise<CreateBidResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<CreateBidResponse>('bid/create-bid', payload);
        resolve(data);
      } catch (error: any) {
        console.log('in bidAnalysisServices > createBid, Err===> ', error);
        // Pass the error with response data intact for toast notification
        reject(error);
      }
    });
  },

  /**
   * Get bid details by ID
   * API: GET /bid/get-bid-details/{bidId}
   * Transforms backend response to normalized frontend format
   */
  getBidDetails: (bidId: number): Promise<BidDetails> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await getRequest<BidDetailsAPIResponse>(`bid/get-bid-details/${bidId}`);

        // Parse template_type from backend format ("NC", "MAS Direct", "MAS Indirect")
        // to extract normalized template_type and product_type
        const { template_type, product_type } = parseTemplateType(data.template_type);

        // Transform to frontend format
        const transformedData: BidDetails = {
          bid_master_id: data.bid_master_id,
          product_name: data.product_name || '',
          analyst: data.analyst || '',
          product_type: product_type,
          template_type: template_type,
          intended_contracts: data.intended_contracts || null,
          target_launch_date: data.target_launch_date || null, // Pass through target launch date from backend
          gsms_handling_type: data.gsms_handling_type || null, // Pass through handling type from backend
          form_details: data.form_details || { form_data: [] },
          isActive: data.isActive || 0,
          analystBADate: data.analystBADate || null,
          status: data.status || 'Draft',
          reviewer: (data as any).reviewer || undefined, // Reviewer email from API
          LastModifiedDate: (data as any).LastModifiedDate || undefined, // Last modified date from API
          approved_by: data.approved_by ?? null, // Bid-level approver for Global Output
          va_data_as_of: (data as any).va_data_as_of ?? null, // Latest VA market sales month/year at bid creation
          dod_data_as_of: (data as any).dod_data_as_of ?? null, // Latest DOD market sales month/year at bid creation
        };

        resolve(transformedData);
      } catch (error) {
        console.log('in bidAnalysisServices > getBidDetails, Err===> ', error);
        reject(error);
      }
    });
  },

  /**
   * Validate bid
   * API: GET /bid/validate/{bidId}
   * Returns validation results grouped by rule_name
   */
  validateBid: (
    bidId: number
  ): Promise<
    Array<{
      rule_name: string;
      status: 'warning' | 'success';
      text: string;
    }>
  > => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await getRequest<{
          data: Array<{
            rule_name: string;
            status: 'warning' | 'success';
            text: string;
          }>;
        }>(`bid/validate/${bidId}`);

        const validationResults = response.data?.data || [];
        resolve(validationResults);
      } catch (error) {
        console.log('in bidAnalysisServices > validateBid, Err===> ', error);
        reject(error);
      }
    });
  },

  /**
   * Get dashboard analytics (using mock data for now)
   */
  getDashboardAnalytics: (): Promise<DashboardAnalytics> => {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve(mockData.dashboardAnalytics as DashboardAnalytics);
      }, 800);
    });
  },

  /**
   * Get all active projects (alias for getActiveBids)
   * @deprecated Use getActiveBids with parameters instead
   */
  getActiveProjects: (): Promise<BidAnalysisProject[]> => {
    return bidAnalysisServices.getActiveBids().then(result => result.data);
  },

  /**
   * Get recent activity (using mock data for now)
   */
  getRecentActivity: (): Promise<Activity[]> => {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve(mockData.recentActivity as Activity[]);
      }, 600);
    });
  },

  /**
   * Get all opportunities - using mock data (will be replaced with real API)
   */
  getOpportunities: (): Promise<Opportunity[]> => {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve(mockData.opportunities as Opportunity[]);
      }, 900);
    });
  },

  /**
   * Create new bid analysis (legacy - use createBid instead)
   */
  createBidAnalysis: (
    _wizardState: BidAnalysisWizardState
  ): Promise<{ id: string; message: string }> => {
    return new Promise(resolve => {
      setTimeout(() => {
        const newId = `BA-${Date.now()}`;
        resolve({
          id: newId,
          message: 'Bid Analysis created successfully!',
        });
      }, 1200);
    });
  },

  /**
   * Get project by ID (using mock data for now)
   */
  getProjectById: (id: string): Promise<BidAnalysisProject | null> => {
    return new Promise(resolve => {
      setTimeout(() => {
        const project = (mockData.activeProjects as BidAnalysisProject[]).find(p => p.id === id);
        resolve(project || null);
      }, 600);
    });
  },

  /**
   * Update project (using mock data for now)
   */
  updateProject: (
    id: string,
    updates: Partial<BidAnalysisProject>
  ): Promise<BidAnalysisProject> => {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        const project = (mockData.activeProjects as BidAnalysisProject[]).find(p => p.id === id);
        if (project) {
          const updatedProject = { ...project, ...updates };
          resolve(updatedProject);
        } else {
          reject(new Error('Project not found'));
        }
      }, 800);
    });
  },

  /**
   * Delete project (using mock data for now)
   */
  deleteProject: (_id: string): Promise<{ message: string }> => {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          message: 'Project deleted successfully',
        });
      }, 600);
    });
  },

  /**
   * Get template data by product type (using mock data for now)
   */
  getTemplateData: (productType: 'direct' | 'indirect'): Promise<BidAnalysisTemplate> => {
    return new Promise(resolve => {
      setTimeout(() => {
        const template =
          productType === 'direct'
            ? templateMockData.directProductTemplate
            : templateMockData.indirectProductTemplate;
        resolve(template as BidAnalysisTemplate);
      }, 1000);
    });
  },

  /**
   * Get all NDC by supplier, concept, and pack size
   * API: POST /bid/get-all-ndc-by-supplier-concept-packsize
   */
  getAllNdcBySupplierConceptPacksize: (params: {
    supplier_name: string;
    concept_name: string;
    packsize: number;
  }): Promise<
    Array<{
      ndc11: string;
      brand_name: string;
      pack_size: number;
      labeler: string;
      concept_name: string;
    }>
  > => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await postRequest<{ data: Array<any>; count: number }>(
          'bid/get-all-ndc-by-supplier-concept-packsize',
          params
        );
        // Handle response structure - API returns { data: [...], count: ... }
        // Axios wraps it, so response.data is { data: [...], count: ... }
        const data = response.data?.data || (Array.isArray(response.data) ? response.data : []);
        resolve(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error getting NDC data:', error);
        reject(error);
      }
    });
  },

  /**
   * Get product detail / current market state by bid ID.
   * When supplier bulk NDC is not selected, payload is { concept_name, pack_size, manufacturer }.
   * When NDC is selected, payload includes mfg_ndc.
   * API: POST /bid/{bidId}/product_detail
   */
  getProductDetail: (
    bidId: number,
    params: {
      concept_name: string;
      pack_size: number;
      manufacturer: string;
      mfg_ndc?: string | null;
    }
  ): Promise<any> => {
    return new Promise(async (resolve, reject) => {
      try {
        const payload =
          params.mfg_ndc != null && params.mfg_ndc !== ''
            ? { ...params, mfg_ndc: params.mfg_ndc }
            : { concept_name: params.concept_name, pack_size: params.pack_size, manufacturer: params.manufacturer };
        const response = await postRequest<any>(`bid/${bidId}/product_detail`, payload);
        // Handle response structure - API returns { data: {...} }
        // Axios wraps it, so response.data is { data: {...} }
        const productDetail = response.data?.data || response.data || response;
        resolve(productDetail);
      } catch (error) {
        console.error('Error getting product detail:', error);
        reject(error);
      }
    });
  },

  /**
   * Get market sales trend by agency
   * API: POST /bid/{bidId}/market-sales-trend-by-agency
   */
  getMarketSalesTrendByAgency: (
    bidId: number,
    params: {
      concept: string;
      pack_size: number;
      labeller?: string | null;
      start_date?: string;
      end_date?: string;
    },
    config?: { signal?: AbortSignal }
  ): Promise<Array<{ month: string; dod_volume: number; va_volume: number; sales: number }>> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await postRequest<{ data: Array<any> }>(
          `bid/${bidId}/market-sales-trend-by-agency`,
          params,
          undefined,
          config
        );
        // Handle response structure - API returns { data: [...] }
        // Axios wraps it, so response.data is { data: [...] }
        const data = response.data?.data || (Array.isArray(response.data) ? response.data : []);
        resolve(Array.isArray(data) ? data : []);
      } catch (error: any) {
        // Don't log error if request was aborted
        if (error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
          reject(error);
          return;
        }
        console.error('Error getting market sales trend:', error);
        reject(error);
      }
    });
  },

  /**
   * Get market share by volume
   * API: POST /bid/{bidId}/market-share-by-volume
   * Returns: Array with { labeller: string, market_share_by_volume_labeller: string, volume: number }
   */
  getMarketShareByVolume: (
    bidId: number,
    params: {
      concept: string;
      pack_size: number;
      start_date?: string;
      end_date?: string;
    },
    config?: { signal?: AbortSignal }
  ): Promise<
    Array<{ labeller: string; market_share_by_volume_labeller: string; volume: number }>
  > => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await postRequest<{ data: Array<any> }>(
          `bid/${bidId}/market-share-by-volume`,
          params,
          undefined,
          config
        );
        // Handle response structure - API returns { data: [...] }
        // Axios wraps it, so response.data is { data: [...] }
        const data = response.data?.data || (Array.isArray(response.data) ? response.data : []);
        resolve(Array.isArray(data) ? data : []);
      } catch (error: any) {
        // Don't log error if request was aborted
        if (error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
          reject(error);
          return;
        }
        console.error('Error getting market share by volume:', error);
        reject(error);
      }
    });
  },

  /**
   * Get volume by class of trade (COT)
   * API: POST /bid/volume-by-cot
   */
  getVolumeByCot: (
    params: {
      concept: string;
      pack_size: number;
      labeller?: string | null;
      start_date?: string;
      end_date?: string;
    },
    config?: { signal?: AbortSignal }
  ): Promise<Array<{ cot: string; volume: number }>> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await postRequest<{ data: Array<any> }>(
          'bid/volume-by-cot',
          params,
          undefined,
          config
        );
        const data = response.data?.data || (Array.isArray(response.data) ? response.data : []);
        resolve(Array.isArray(data) ? data : []);
      } catch (error: any) {
        if (error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
          reject(error);
          return;
        }
        console.error('Error getting volume by COT:', error);
        reject(error);
      }
    });
  },

  /**
   * Get contract price ranking
   * API: POST /bid/{bidId}/contract-price-ranking
   */
  getContractPriceRanking: async (
    bidId: number,
    params: {
      concept_name: string;
      pack_size: number;
      start_date?: string;
      end_date?: string;
    },
    config?: { signal?: AbortSignal }
  ): Promise<
    Array<{
      ndc: string;
      contracter_name: string | null; // API can return null
      labeller_name: string;
      price_type: string;
      price: number;
      price_eu: number;
      current_wac: number | null; // API can return null
      current_market_share: string | null; // API returns string like "0.78%" or can be null
      extra_data: {
        contract_start_date: string | null;
        percentage_of_commercial_market: string | null;
        percentage_of_government_market: string | null;
        nad: string | null;
        nad_expiry: string | null;
      };
      price_rank: number;
    }>
  > => {
    try {
      // Check if request was aborted
      if (config?.signal?.aborted) {
        throw new Error('Request aborted');
      }

      const response = await postRequest<{ data: Array<any> }>(
        `bid/${bidId}/contract-price-ranking`,
        params,
        undefined,
        config
      );

      // postRequest returns AxiosResponse<T>, so the actual response body is in response.data
      // API returns { data: [...] }, so we need response.data.data to get the array
      let data: Array<any> = [];
      
      // Extract the array from the response structure
      // AxiosResponse structure: { data: { data: [...] } }
      // So response.data is the API response body: { data: [...] }
      // And response.data.data is the actual array: [...]
      if (response?.data) {
        // Check if response.data has a nested data property (API structure: { data: [...] })
        if (response.data.data && Array.isArray(response.data.data)) {
          data = response.data.data;
        }
        // Check if response.data is the array directly (fallback)
        else if (Array.isArray(response.data)) {
          data = response.data;
        }
      }
      
      // Ensure all items have the correct structure and preserve all fields from API
      // Map each item preserving all original values exactly as received from API
      const mappedData = data.map((item: any) => {
        // Preserve all values exactly as they come from the API
        // Only use defaults if the field is truly missing (undefined)
        return {
          ndc: item.ndc !== undefined ? item.ndc : '',
          contracter_name: item.contracter_name !== undefined ? item.contracter_name : null, // Preserve "GSMS", null, or empty string
          labeller_name: item.labeller_name !== undefined ? item.labeller_name : '',
          price_type: item.price_type !== undefined ? item.price_type : '',
          price: item.price !== undefined ? item.price : 0,
          price_eu: item.price_eu !== undefined ? item.price_eu : 0,
          current_wac: item.current_wac !== undefined ? item.current_wac : null, // Preserve null or number
          current_market_share: item.current_market_share !== undefined ? item.current_market_share : null, // Preserve "1.03%", "0.00%", etc.
          extra_data: item.extra_data !== undefined ? item.extra_data : {
            contract_start_date: null,
            percentage_of_commercial_market: null,
            percentage_of_government_market: null,
            nad: null,
            nad_expiry: null,
          },
          price_rank: item.price_rank !== undefined ? item.price_rank : 0,
        };
      });
      
      return mappedData;
    } catch (error: any) {
      // Don't log error if request was aborted/canceled
      if (
        error?.name === 'AbortError' ||
        error?.name === 'CanceledError' ||
        error?.code === 'ERR_CANCELED'
      ) {
        throw error;
      }
      console.error('Error getting contract price ranking:', error);
      throw error;
    }
  },

  /**
   * Get all intended contracts
   * API: GET /bid/get-all-intended-contracts
   */
  getAllIntendedContracts: (): Promise<Array<{ id: number; contract_name: string }>> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await getRequest<{ data: Array<any>; count: number }>(
          'bid/get-all-intended-contracts'
        );
        // API returns { data: [...], count: ... }
        // Axios wraps it, so response.data is { data: [...], count: ... }
        const data = response.data?.data || (Array.isArray(response.data) ? response.data : []);
        resolve(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error getting contracts:', error);
        reject(error);
      }
    });
  },

  /**
   * Get all GSMS handling types
   * API: GET /bid/get-all-gsms-handling-types
   */
  getAllGsmsHandlingTypes: (): Promise<Array<{ id: number; handling_type: string }>> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await getRequest<{ data: Array<any>; count: number }>(
          'bid/get-all-gsms-handling-types'
        );
        // API returns { data: [...], count: ... }
        const data = response.data?.data || response.data || [];
        resolve(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error getting handling types:', error);
        reject(error);
      }
    });
  },

  /**
   * Get suppliers for bid
   * API: GET /bid/get-suppliers-for-bid/{bidId}
   */
  getSuppliersForBid: (bidId: number): Promise<string[]> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await getRequest<{ suppliers: string[] }>(
          `bid/get-suppliers-for-bid/${bidId}`
        );
        // API returns { suppliers: [...] }
        const suppliers = (response.data as any)?.suppliers || [];
        resolve(Array.isArray(suppliers) ? suppliers : []);
      } catch (error) {
        console.error('Error getting suppliers:', error);
        reject(error);
      }
    });
  },

  /**
   * Save bid data
   * API: POST /bid/save-bid/{bidId}
   */
  saveBid: (bidId: number, payload: any): Promise<any> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await postRequest<any>(`bid/save-bid/${bidId}/`, payload);
        resolve(response.data || response);
      } catch (error) {
        console.error('Error saving bid:', error);
        reject(error);
      }
    });
  },

  /**
   * Get all notes for a bid
   * API: GET /bid/{bidId}/notes
   */
  getNotes: (bidId: number): Promise<BidNote[]> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await getRequest<{ data: BidNote[] }>(`bid/${bidId}/notes`);
        const data = response.data?.data || (Array.isArray(response.data) ? response.data : []);
        resolve(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error getting notes:', error);
        reject(error);
      }
    });
  },

  /**
   * Create a new note
   * API: POST /bid/notes/create
   */
  createNote: (payload: CreateNoteRequest): Promise<BidNote> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await postRequest<{ data: BidNote }>('bid/notes/create', payload);
        const data = response.data?.data || response.data;
        resolve(data);
      } catch (error) {
        console.error('Error creating note:', error);
        reject(error);
      }
    });
  },

  /**
   * Update an existing note
   * API: PUT /bid/notes/{noteId}/update
   */
  updateNote: (noteId: number, payload: UpdateNoteRequest): Promise<BidNote> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await putRequest<{ data: BidNote }>(`bid/notes/${noteId}/update`, payload);
        const data = response.data?.data || response.data;
        resolve(data);
      } catch (error) {
        console.error('Error updating note:', error);
        reject(error);
      }
    });
  },

  /**
   * Delete a note (soft delete)
   * API: DELETE /bid/notes/{noteId}/delete
   */
  deleteNote: (noteId: number, lastModifiedBy: string): Promise<{ message: string }> => {
    return new Promise(async (resolve, reject) => {
      try {
        // For DELETE with body, we need to use POST as a workaround
        // since some servers don't support DELETE with body
        const response = await postRequest<{ message: string }>(`bid/notes/${noteId}/delete`, {
          last_modified_by: lastModifiedBy,
        });
        resolve(response.data || { message: 'Note deleted successfully' });
      } catch (error) {
        console.error('Error deleting note:', error);
        reject(error);
      }
    });
  },

  /**
   * Change bid status
   * API: POST /bid/{bidId}/change-status
   */
  changeBidStatus: (
    bidId: number,
    status: string
  ): Promise<{ message: string; bid_id: number; old_status?: string; new_status: string }> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await postRequest<{
          message: string;
          bid_id: number;
          old_status?: string;
          new_status: string;
        }>(`bid/${bidId}/change-status`, { status });
        resolve(response.data || response);
      } catch (error) {
        console.error('Error changing bid status:', error);
        reject(error);
      }
    });
  },

  /**
   * Send bid for review (changes status to "In Review" and runs validation)
   * API: POST /bid/{bidId}/send-for-review
   */
  sendForReview: (
    bidId: number,
    reviewerEmail: string
  ): Promise<{
    message: string;
    bid_id: number;
    reviewer_email: string;
    status: string;
    reviewer_assignment_id: number;
    validation_results: Array<{
      rule_name: string;
      status: 'warning' | 'success' | 'error';
      text: string;
    }>;
  }> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await postRequest<{
          message: string;
          bid_id: number;
          reviewer_email: string;
          status: string;
          reviewer_assignment_id: number;
          validation_results: Array<{
            rule_name: string;
            status: 'warning' | 'success' | 'error';
            text: string;
          }>;
        }>(`bid/${bidId}/send-for-review`, {
          reviewer_email: reviewerEmail,
        });
        resolve(response.data || response);
      } catch (error) {
        console.error('Error sending bid for review:', error);
        reject(error);
      }
    });
  },

  /**
   * Get audit trail for a bid
   * API: GET /bid/{bidId}/audit-trail/list
   */
  getAuditTrail: (
    bidId: number,
    params?: {
      page?: number;
      page_size?: number;
    }
  ): Promise<{
    data: AuditTrailEntry[];
    pagination: {
      total_count: number;
      total_pages: number;
      has_next: boolean;
      has_previous: boolean;
    };
  }> => {
    return new Promise(async (resolve, reject) => {
      try {
        // Build query parameters
        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append('page', String(params.page));
        if (params?.page_size) queryParams.append('page_size', String(params.page_size));

        const url = `bid/${bidId}/audit-trail/list${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        const response = await getRequest<{
          data: AuditTrailEntry[];
          pagination: {
            total_count: number;
            total_pages: number;
            has_next: boolean;
            has_previous: boolean;
          };
        }>(url);

        // Handle response structure - API returns { data: [...], pagination: {...} }
        const data = response.data?.data || [];
        const pagination = response.data?.pagination || {
          total_count: 0,
          total_pages: 0,
          has_next: false,
          has_previous: false,
        };

        resolve({ data, pagination });
      } catch (error) {
        console.error('Error getting audit trail:', error);
        reject(error);
      }
    });
  },

  /**
   * Get latest audit trail entries across all bids
   * API: GET /bid/audit-trail/latest?limit=10&page=1&page_size=10
   */
  getLatestAuditTrail: (params?: {
    limit?: number;
    page?: number;
    page_size?: number;
  }): Promise<{
    data: AuditTrailEntry[];
    pagination?: {
      total_count: number;
      total_pages: number;
      has_next: boolean;
      has_previous: boolean;
    };
  }> => {
    return new Promise(async (resolve, reject) => {
      try {
        // Build query parameters
        const queryParams = new URLSearchParams();
        if (params?.limit) queryParams.append('limit', String(params.limit));
        if (params?.page) queryParams.append('page', String(params.page));
        if (params?.page_size) queryParams.append('page_size', String(params.page_size));

        const url = `bid/audit-trail/latest${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        const response = await getRequest<{
          data: AuditTrailEntry[];
          pagination?: {
            total_count: number;
            total_pages: number;
            has_next: boolean;
            has_previous: boolean;
          };
        }>(url);

        // Handle response structure
        const data = response.data?.data || [];
        const pagination = response.data?.pagination;

        resolve({ data, pagination });
      } catch (error) {
        console.error('Error getting latest audit trail:', error);
        reject(error);
      }
    });
  },

  /**
   * Get supplier discount
   * API: POST /bid/supplier-discounts/total-discount
   */
  getSupplierDiscount: (
    supplierCode: string
  ): Promise<{
    supplier_code: string;
    total_discount: number;
  }> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await postRequest<{
          data: {
            supplier_code: string;
            total_discount: number;
          };
        }>(`bid/supplier-discounts/total-discount`, {
          supplier_code: supplierCode,
        });

        // Handle response structure
        const data = response.data?.data || response.data;
        if (data) {
          resolve(data);
        } else {
          reject(new Error('Invalid response format'));
        }
      } catch (error: any) {
        // If 404, return null to indicate no discount found
        if (error?.response?.status === 404) {
          reject({ is404: true, error });
        } else {
          console.error('Error getting supplier discount:', error);
          reject(error);
        }
      }
    });
  },

  /**
   * Get all supplier discounts (reference table)
   * API: GET /bid/supplier-discounts
   */
  getAllSupplierDiscounts: (): Promise<SupplierDiscount[]> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await getRequest<{ data: SupplierDiscount[] }>(
          'bid/supplier-discounts'
        );
        const data = response.data?.data || (Array.isArray(response.data) ? response.data : []);
        resolve(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error getting supplier discounts:', error);
        reject(error);
      }
    });
  },

  /**
   * Create a new supplier discount
   * API: POST /bid/supplier-discounts/create
   */
  createSupplierDiscount: (payload: CreateSupplierDiscountRequest): Promise<SupplierDiscount> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await postRequest<{ data: SupplierDiscount }>(
          'bid/supplier-discounts/create',
          payload
        );
        const data = response.data?.data || response.data;
        resolve(data);
      } catch (error) {
        console.error('Error creating supplier discount:', error);
        reject(error);
      }
    });
  },

  /**
   * Update an existing supplier discount
   * API: PATCH /bid/supplier-discounts/{id}/update
   */
  updateSupplierDiscount: (
    discountId: number,
    payload: UpdateSupplierDiscountRequest
  ): Promise<SupplierDiscount> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await patchRequest<{ data: SupplierDiscount }>(
          `bid/supplier-discounts/${discountId}/update`,
          payload
        );
        const data = response.data?.data || response.data;
        resolve(data);
      } catch (error) {
        console.error('Error updating supplier discount:', error);
        reject(error);
      }
    });
  },

  /**
   * Delete a supplier discount
   * API: DELETE /bid/supplier-discounts/{id}/delete
   */
  deleteSupplierDiscount: (discountId: number): Promise<{ message: string }> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await deleteRequest<{ message: string }>(
          `bid/supplier-discounts/${discountId}/delete`
        );
        resolve(response.data || { message: 'Supplier discount deleted successfully' });
      } catch (error) {
        console.error('Error deleting supplier discount:', error);
        reject(error);
      }
    });
  },

  /**
   * Get all attributes
   * API: GET /bid/assignment-rules/attributes
   */
  getAllAttributes: (): Promise<Attribute[]> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await getRequest<{ data: Attribute[] }>(
          'bid/assignment-rules/attributes'
        );
        const data = response.data?.data || (Array.isArray(response.data) ? response.data : []);
        resolve(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error getting attributes:', error);
        reject(error);
      }
    });
  },

  /**
   * Create a new attribute
   * API: POST /bid/assignment-rules/attributes/create
   */
  createAttribute: (payload: CreateAttributeRequest): Promise<Attribute> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await postRequest<{ data: Attribute }>(
          'bid/assignment-rules/attributes/create',
          payload
        );
        const data = response.data?.data || response.data;
        resolve(data);
      } catch (error) {
        console.error('Error creating attribute:', error);
        reject(error);
      }
    });
  },

  /**
   * Update an existing attribute
   * API: PATCH /bid/assignment-rules/attributes/{id}/update
   */
  updateAttribute: (
    attributeId: number,
    payload: UpdateAttributeRequest
  ): Promise<Attribute> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await patchRequest<{ data: Attribute }>(
          `bid/assignment-rules/attributes/${attributeId}/update`,
          payload
        );
        const data = response.data?.data || response.data;
        resolve(data);
      } catch (error) {
        console.error('Error updating attribute:', error);
        reject(error);
      }
    });
  },

  /**
   * Delete an attribute
   * API: DELETE /bid/assignment-rules/attributes/{id}/delete
   */
  deleteAttribute: (attributeId: number): Promise<{ message: string }> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await deleteRequest<{ message: string }>(
          `bid/assignment-rules/attributes/${attributeId}/delete`
        );
        resolve(response.data || { message: 'Attribute deleted successfully' });
      } catch (error) {
        console.error('Error deleting attribute:', error);
        reject(error);
      }
    });
  },

  /**
   * Get users by attribute
   * API: GET /bid/assignment-rules/attributes/{id}/users
   */
  getUsersByAttribute: (attributeId: number): Promise<UserAttributeMapping[]> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await getRequest<{ data: UserAttributeMapping[] }>(
          `bid/assignment-rules/attributes/${attributeId}/users`
        );
        const data = response.data?.data || (Array.isArray(response.data) ? response.data : []);
        resolve(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error getting users by attribute:', error);
        reject(error);
      }
    });
  },

  /**
   * Get all attributes with their users
   * API: GET /bid/assignment-rules/attributes/with-users
   */
  getAttributesWithUsers: (): Promise<AttributeWithUsers[]> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await getRequest<{ data: AttributeWithUsers[] }>(
          'bid/assignment-rules/attributes/with-users'
        );
        const data = response.data?.data || (Array.isArray(response.data) ? response.data : []);
        resolve(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error getting attributes with users:', error);
        reject(error);
      }
    });
  },

  /**
   * Get attributes by user
   * API: GET /bid/assignment-rules/users/{userId}/attributes
   */
  getAttributesByUser: (userId: string): Promise<UserAttributeMapping[]> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await getRequest<{ data: UserAttributeMapping[] }>(
          `bid/assignment-rules/users/${userId}/attributes`
        );
        const data = response.data?.data || (Array.isArray(response.data) ? response.data : []);
        resolve(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error getting attributes by user:', error);
        reject(error);
      }
    });
  },

  /**
   * Add users to an attribute (bulk)
   * API: POST /bid/assignment-rules/attributes/{id}/users/add
   */
  addUsersToAttribute: (
    attributeId: number,
    payload: AddUsersToAttributeRequest
  ): Promise<{ message: string; data?: UserAttributeMapping[] }> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await postRequest<{ message: string; data?: UserAttributeMapping[] }>(
          `bid/assignment-rules/attributes/${attributeId}/users/add`,
          payload
        );
        resolve(response.data || { message: 'Users added successfully' });
      } catch (error) {
        console.error('Error adding users to attribute:', error);
        reject(error);
      }
    });
  },

  /**
   * Remove users from an attribute (bulk)
   * API: DELETE /bid/assignment-rules/attributes/{id}/users/remove
   * Note: DELETE with body - using axios directly since deleteRequest doesn't support body
   */
  removeUsersFromAttribute: (
    attributeId: number,
    payload: AddUsersToAttributeRequest
  ): Promise<{ message: string }> => {
    return new Promise(async (resolve, reject) => {
      try {
        // DELETE with body - need to use axios directly
        const response = await axiosClient.delete<{ message: string }>(
          `/bid/assignment-rules/attributes/${attributeId}/users/remove`,
          { data: payload }
        );
        resolve(response.data || { message: 'Users removed successfully' });
      } catch (error) {
        console.error('Error removing users from attribute:', error);
        reject(error);
      }
    });
  },

  /**
   * Get all assignment rules
   * API: GET /bid/assignment-rules
   */
  getAllRules: (): Promise<AssignmentRule[]> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await getRequest<{ data: AssignmentRule[] }>(
          'bid/assignment-rules'
        );
        const data = response.data?.data || (Array.isArray(response.data) ? response.data : []);
        resolve(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error getting assignment rules:', error);
        reject(error);
      }
    });
  },

  /**
   * Create a new assignment rule
   * API: POST /bid/assignment-rules/create
   */
  createRule: (payload: CreateRuleRequest): Promise<AssignmentRule> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await postRequest<{ data: AssignmentRule }>(
          'bid/assignment-rules/create',
          payload
        );
        const data = response.data?.data || response.data;
        resolve(data);
      } catch (error) {
        console.error('Error creating rule:', error);
        reject(error);
      }
    });
  },

  /**
   * Update an existing assignment rule
   * API: PUT /bid/assignment-rules/{id}/update
   */
  updateRule: (
    ruleId: number,
    payload: UpdateRuleRequest
  ): Promise<AssignmentRule> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await putRequest<{ data: AssignmentRule }>(
          `bid/assignment-rules/${ruleId}/update`,
          payload
        );
        const data = response.data?.data || response.data;
        resolve(data);
      } catch (error) {
        console.error('Error updating rule:', error);
        reject(error);
      }
    });
  },

  /**
   * Delete an assignment rule
   * API: DELETE /bid/assignment-rules/{id}/delete
   */
  deleteRule: (ruleId: number): Promise<{ message: string }> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await deleteRequest<{ message: string }>(
          `bid/assignment-rules/${ruleId}/delete`
        );
        resolve(response.data || { message: 'Rule deleted successfully' });
      } catch (error) {
        console.error('Error deleting rule:', error);
        reject(error);
      }
    });
  },

  /**
   * Get eligible users based on assignment rules
   * API: POST /bid/assignment-rules/get-eligible-users
   */
  getEligibleUsers: (payload: EligibleUsersRequest): Promise<GetEligibleUsersResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await postRequest<{ data: GetEligibleUsersResponse }>(
          'bid/assignment-rules/get-eligible-users',
          payload
        );
        const data = response.data?.data || response.data;
        resolve(data);
      } catch (error) {
        console.error('Error fetching eligible users:', error);
        reject(error);
      }
    });
  },

  /**
   * Get manager dashboard bids with pagination, search, and filters
   * API: GET /bid/manager/dashboard?page=1&page_size=10&search=...&status=...&analyst=...&template_name=...&reviewer=...
   */
  getManagerDashboard: (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    status?: string;
    analyst?: string;
    template_name?: string;
    reviewer?: string;
  }): Promise<{
    data: any[];
    pagination: {
      page: number;
      page_size: number;
      total_count: number;
      total_pages: number;
      has_next: boolean;
      has_previous: boolean;
    };
  }> => {
    return new Promise(async (resolve, reject) => {
      try {
        // Build query parameters
        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append('page', String(params.page));
        if (params?.page_size) queryParams.append('page_size', String(params.page_size));
        if (params?.search) queryParams.append('search', params.search);
        if (params?.status) queryParams.append('status', params.status);
        if (params?.analyst) queryParams.append('analyst', params.analyst);
        if (params?.template_name) queryParams.append('template_name', params.template_name);
        if (params?.reviewer) queryParams.append('reviewer', params.reviewer);

        const url = `bid/manager/dashboard${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        const response = await getRequest<any>(url);

        // Extract data and pagination
        const data = response.data?.data || [];
        const pagination = response.data?.pagination || {
          page: params?.page || 1,
          page_size: params?.page_size || 10,
          total_count: 0,
          total_pages: 0,
          has_next: false,
          has_previous: false,
        };

        resolve({ data, pagination });
      } catch (error) {
        console.error('Error fetching manager dashboard:', error);
        reject(error);
      }
    });
  },

  /**
   * Get approved bids with pagination, search, and filters
   * API: GET /bid/manager/approved-bids?page=1&page_size=10&search=...&analyst=...&reviewer=...&created_by=...&template_name=...
   */
  getApprovedBids: (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    analyst?: string;
    reviewer?: string;
    created_by?: string;
    template_name?: string;
  }): Promise<{
    data: any[];
    pagination: {
      page: number;
      page_size: number;
      total_count: number;
      total_pages: number;
      has_next: boolean;
      has_previous: boolean;
    };
  }> => {
    return new Promise(async (resolve, reject) => {
      try {
        // Build query parameters
        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append('page', String(params.page));
        if (params?.page_size) queryParams.append('page_size', String(params.page_size));
        if (params?.search) queryParams.append('search', params.search);
        if (params?.analyst) queryParams.append('analyst', params.analyst);
        if (params?.reviewer) queryParams.append('reviewer', params.reviewer);
        if (params?.created_by) queryParams.append('created_by', params.created_by);
        if (params?.template_name) queryParams.append('template_name', params.template_name);

        const url = `bid/manager/approved-bids${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        const response = await getRequest<any>(url);

        // Extract data and pagination
        const data = response.data?.data || [];
        const pagination = response.data?.pagination || {
          page: params?.page || 1,
          page_size: params?.page_size || 10,
          total_count: 0,
          total_pages: 0,
          has_next: false,
          has_previous: false,
        };

        resolve({ data, pagination });
      } catch (error) {
        console.error('Error fetching approved bids:', error);
        reject(error);
      }
    });
  },

  /**
   * Mark bid as needing modification
   * API: POST /bid/{bidId}/mark-needs-modification
   */
  markNeedsModification: (bidId: number, note: string): Promise<any> => {
    return new Promise(async (resolve, reject) => {
      try {
        const url = `bid/${bidId}/mark-needs-modification`;
        const response = await postRequest<any>(url, { note });
        resolve(response.data);
      } catch (error) {
        console.error('Error marking bid as needs modification:', error);
        reject(error);
      }
    });
  },

  /**
   * Mark bid as approved
   * API: POST /bid/{bidId}/mark-approved
   */
  markApproved: (bidId: number, note?: string): Promise<any> => {
    return new Promise(async (resolve, reject) => {
      try {
        const url = `bid/${bidId}/mark-approved`;
        // Always send note field, even if empty string
        const payload = { note: note || '' };
        const response = await postRequest<any>(url, payload);
        resolve(response.data);
      } catch (error) {
        console.error('Error marking bid as approved:', error);
        reject(error);
      }
    });
  },

  /**
   * Get market landscape for a bid
   * API: GET /bid/market-landscape/<bid-id>
   */
  getMarketLandscape: (bidId: number): Promise<{ success: boolean; data: MarketLandscapeItem[] }> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await getRequest<{ success: boolean; data: MarketLandscapeItem[] }>(
          `bid/market-landscape/${bidId}/`
        );
        const data = response.data?.data || [];
        const success = response.data?.success ?? true;
        resolve({ success, data: Array.isArray(data) ? data : [] });
      } catch (error) {
        console.error('Error getting market landscape:', error);
        reject(error);
      }
    });
  },

  /**
   * Get market landscape labellers by molecule and form
   * API: GET /bid/market-landscape/labellers/?molecule_and_form=<product name>
   */
  getMarketLandscapeLabellers: (
    moleculeAndForm: string
  ): Promise<{ success: boolean; data: MarketLandscapeItem[] }> => {
    return new Promise(async (resolve, reject) => {
      try {
        const queryParams = new URLSearchParams();
        queryParams.append('molecule_and_form', moleculeAndForm);
        const url = `bid/market-landscape/labellers/?${queryParams.toString()}`;
        const response = await getRequest<{ success: boolean; data: MarketLandscapeItem[] }>(url);
        const data = response.data?.data || [];
        const success = response.data?.success ?? true;
        resolve({ success, data: Array.isArray(data) ? data : [] });
      } catch (error) {
        console.error('Error getting market landscape labellers:', error);
        reject(error);
      }
    });
  },

  /**
   * Update market landscape for a bid
   * API: PUT /bid/market-landscape/<bid-id>
   */
  updateMarketLandscape: (
    bidId: number,
    payload: { market_landscape: MarketLandscapeItem[] }
  ): Promise<{ success: boolean; message?: string }> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await putRequest<{ success: boolean; message?: string }>(
          `bid/market-landscape/${bidId}/`,
          payload
        );
        resolve(response.data || { success: true });
      } catch (error) {
        console.error('Error updating market landscape:', error);
        reject(error);
      }
    });
  },

};

// Notes interfaces
export interface BidNote {
  id: number;
  bid_master_id: number;
  section_name: string;
  content: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  last_modified_by: string;
  last_modified_date: string;
  is_edited: boolean;
  is_active: boolean;
}

export interface CreateNoteRequest {
  bid_master_id: number;
  section_name: string;
  content: string;
  // created_by should be taken from authenticated user session on backend, not from client
}

export interface UpdateNoteRequest {
  content: string;
  // last_modified_by should be taken from authenticated user session on backend, not from client
}

// Audit Trail interfaces
export interface AuditTrailEntry {
  id: number;
  event_type: string;
  description: string;
  performed_by: string;
  performed_at: string;
  additional_data: any;
}

// Supplier Discount (reference table)
export interface SupplierDiscount {
  id: number;
  supplier: string;
  supplier_name: string;
  supplier_code: string;
  prompt_pay_discount: number | null;
  admin_fee: number | null;
  last_modified_by?: string;
  last_modified_date?: string;
  is_active?: boolean;
}

export interface CreateSupplierDiscountRequest {
  supplier: string;
  supplier_name: string;
  supplier_code: string;
  prompt_pay_discount?: number | null;
  admin_fee?: number | null;
}

export interface UpdateSupplierDiscountRequest {
  supplier?: string;
  supplier_name?: string;
  supplier_code?: string;
  prompt_pay_discount?: number | null;
  admin_fee?: number | null;
}

// Attribute interfaces
export interface Attribute {
  id: number;
  attribute_name: string;
  attribute_description: string;
  is_active: boolean;
  last_modified_by: string;
  last_modified_date: string;
}

export interface CreateAttributeRequest {
  attribute_name: string;
  attribute_description: string;
}

export interface UpdateAttributeRequest {
  attribute_name?: string;
  attribute_description?: string;
}

// User-Attribute Mapping interfaces
export interface UserAttributeMapping {
  id: number;
  user_id: string;
  attribute_id: number;
  attribute_name: string;
  is_active: boolean;
  last_modified_by: string;
  last_modified_date: string;
}

export interface AttributeWithUsers {
  id: number;
  attribute_name: string;
  attribute_description: string;
  is_active: boolean;
  last_modified_by: string;
  last_modified_date: string;
  users: Array<{
    user_id: string;
    user_details: {
      id: number;
      name: string;
      email: string;
      dept_id: number;
      department_name: string;
    };
    mapping_id: number;
    last_modified_by: string;
    last_modified_date: string;
  }>;
  user_count: number;
}

export interface UserAttributeMappingDisplay {
  mapping_id: number;
  attribute_id: number;
  attribute_name: string;
  user_id: string;
  user_name: string;
  user_email: string;
  department_name: string;
  last_modified_by: string;
  last_modified_date: string;
}

export interface AddUsersToAttributeRequest {
  user_ids: string[];
}

// Assignment Rules interfaces
export interface RuleCondition {
  field: 'profitPercentage' | 'gpPerUnit';
  operator: '>' | '<' | '>=' | '<=' | '=' | 'between';
  value: string; // For "between": "25-35", otherwise numeric string
}

export interface RuleJSON {
  contract_type: 'MAS direct' | 'MAS indirect' | 'NC';
  combinator: 'and' | 'or';
  rules: RuleCondition[];
}

export interface AssignmentRule {
  id: number;
  rule_id: string;
  rule_name: string;
  rule_json: RuleJSON;
  special_notes: string;
  attributes: Array<{ id: number; attribute_name: string }>;
  is_active: boolean;
  last_modified_by: string;
  last_modified_date: string;
}

export interface CreateRuleRequest {
  rule_id: string;
  rule_name: string;
  rule_json: RuleJSON;
  special_notes: string;
  attribute_ids: number[];
}

export interface UpdateRuleRequest {
  rule_name?: string;
  rule_json?: RuleJSON;
  special_notes?: string;
  attribute_ids?: number[];
}

// Eligible Users interfaces
export interface EligibleUsersRequest {
  contract_type: string; // "MAS direct", "MAS indirect", or "NC"
  gpPerUnit: number | null;
  profitPercentage: number | null;
}

export interface EligibleUserResponse {
  user_id: string;
  username: string;
  attributes: Array<{
    attribute_id: number;
    attribute_name: string;
  }>;
}

export interface GetEligibleUsersResponse {
  matched_rules: Array<{
    rule_id: string;
    rule_name: string;
    rule_json: RuleJSON;
  }>;
  eligible_users: EligibleUserResponse[];
  summary: {
    total_matched_rules: number;
    total_eligible_users: number;
    contract_type: string;
  };
}

// Market Landscape interfaces
export interface MarketLandscapeItem {
  supplier: string;
  api: string;
  mfg_co: string;
  note: string;
  is_compliant: boolean | null;
}